<template>
    <v-form v-model="isValid" ref="form">
        <vertical-form-table v-model="inputs" v-bind="{ items }" @input="emit">
            <template slot="비밀번호 확인">
                <v-password-confirm-field v-model="isPasswordConfirmed" :password="inputs.password" outlined />
            </template>
            <template slot="연락처">
                <user-contact-certification v-model="inputs.phone" @input="emit" outlined dense :btnAttrs="{ color: 'grey-6 white--text', className: 'w-80px w-lg-100px mt-n8' }" />
            </template>
            <template slot="주소">
                <v-address-field v-model="inputs" outlined dense :btnAttrs="{ color: 'grey-6 white--text', className: 'w-80px w-lg-100px mt-n8' }" @input="emit" />
            </template>
        </vertical-form-table>

        <!-- 약관동의 -->
        <terms-of-agreements-short v-model="inputs._terms" @isValid="(isValid) => (isTermsValid = isValid)" @input="emit" />

        <div class="v-btn-group">
            <v-btn outlined color="grey-d" @click="$emit('prev')" class="min-w-140px min-w-lg-160px"><span class="grey-6--text">취소</span></v-btn>
            <v-btn color="primary" @click="join" class="min-w-140px min-w-lg-160px"><span>가입하기</span></v-btn>
        </div>
    </v-form>
</template>

<script>
import api from "@/api";
import Axios from "axios";
import { mapActions } from "vuex";
import CryptoAES from "@/plugins/crypto-aes";
import { attrs_input, GENDERS, rules, USER_TYPES } from "@/assets/variables";

import VAddressField from "@/components/dumb/v-address-field.vue";
import VerticalFormTable from "@/components/dumb/vertical-form-table.vue";
import VPasswordConfirmField from "@/components/plugins/vuetify/v-password-confirm-field.vue";
import TermsOfAgreementsShort from "@/components/client/join/terms-of-agreements-short.vue";
import UserContactCertification from "@/components/dumb/user-contact-certification.vue";

const { PERSON } = USER_TYPES;
const genders = Object.values(GENDERS);

const initInputs = () => ({
    _terms: [],

    username: null,
    password: null,

    name: null,
    phone: null,
    bornAt: null,
    gender: null,
    code: PERSON.value,
});

const items = [
    {
        key: "username",
        term: "아이디",
        type: "username",
        required: true,
        placeholder: "아이디를 입력하세요",
        rules: rules.username,
    },
    {
        key: "password",
        term: "비밀번호",
        type: "password",
        required: true,
        placeholder: "비밀번호를 입력하세요",
        rules: rules.password,
        autocomplete: "new-password",
    },
    {
        key: "password",
        term: "비밀번호 확인",
        required: true,
    },
    {
        key: "name",
        term: "이름",
        type: "text",
        required: true,
        rules: rules[PERSON.value]?.name,
    },
    {
        key: "bornAt",
        term: "생년월일",
        type: "date",
        required: true,
        rules: rules[PERSON.value]?.bornAt,
    },
    {
        key: "gender",
        term: "성별",
        type: "select",
        items: genders,
        class: "person-baseinfo-form-gender",
        menuProps: { attach: ".person-baseinfo-form-gender" },
        required: true,
        attach: true,
    },
    {
        key: "phone",
        term: "연락처",
        required: true,
    },
    {
        key: "email",
        term: "이메일",
        type: "email",
        required: true,
        rules: rules.email,
    },
    {
        key: "address",
        term: "주소",
        required: true,
    },
].map((item) => ({ ...item, outlined: true, attach: true }));

export default {
    components: {
        VAddressField,
        VerticalFormTable,
        VPasswordConfirmField,
        TermsOfAgreementsShort,
        UserContactCertification,
    },
    props: {
        isDialog: { type: Boolean, default: false },
    },
    data: () => ({
        rules,
        PERSON,
        genders,
        attrs_input,
        items,

        inputs: initInputs(),
        isValid: true,
        isTermsValid: undefined,
        isPasswordConfirmed: undefined,
    }),
    computed: {
        accessToken() {
            return this.$route.query.accessToken;
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        ...mapActions(["login"]),

        async init() {
            if (this.accessToken) {
                try {
                    const { user = {} } = (await Axios.get("/api/v1/me", { headers: { Authorization: `Bearer ${this.accessToken}` } }))?.data || {};
                    let { _id, bornAt, gender } = user;
                    bornAt = bornAt?.toDate?.() || bornAt;
                    this.inputs = { ...initInputs(), _id, bornAt, gender };
                } catch {
                    if (!this.isDialog) this.$router.push("/join/intro");
                }
            } else {
                this.inputs = initInputs();
            }
        },

        async join() {
            if (!this.validates()) return;
            const inputs = {
                ...this.inputs,
                password: CryptoAES.encrypt(this.inputs.password),
            };
            await api.v1.users.post(inputs);

            if (!this.isDialog) this.$router.push("/join/complete");
            else {
                let { username, password } = this.inputs;
                await this.login({ username, password });
                this.$emit("next");
            }
        },

        emit() {
            this.inputs = { ...this.inputs };
        },

        validates() {
            try {
                if (!this.inputs.username) throw new Error("아이디를 입력해주세요");
                if (!this.inputs.password) throw new Error("비밀번호를 입력해주세요");
                if (!this.isPasswordConfirmed) throw new Error("비밀번호를 확인해주세요");
                if (!this.inputs.name) throw new Error("이름을 입력해주세요");
                if (!this.inputs.bornAt) throw new Error("생년월일을 입력해주세요");
                if (!this.inputs.phone) throw new Error("연락처를 입력해주세요");
                if (!this.inputs.email) throw new Error("이메일을 입력해주세요");
                if (!this.inputs.postcode) throw new Error("기본주소를 입력해주세요");
                if (!this.inputs.address1) throw new Error("기본주소를 입력해주세요");
                if (!this.inputs.address2) throw new Error("상세주소를 입력해주세요");

                switch (this.isTermsValid) {
                    case undefined:
                        throw new Error("이용약관을 확인해주세요");
                    case false:
                        throw new Error("필수 이용약관에 모두 동의해주세요");
                    default:
                        break;
                }
            } catch (error) {
                alert(error.message);
                return false;
            }
            return true;
        },
    },
};
</script>
